import { LinkedAccountStatus, useCustomerDetails } from '@egw/api'
import { AlertErrorIcon, Button, ButtonVariant, Link, ModalDrawer, PageHeader } from '@egw/components'
import { configClient } from '@egw/config'
import { WEALTH_ROUTES } from '@egw/constants'
import { sendEvent } from '@egw/events'
import { useTranslation } from '@egw/i18n'
import { BriefcaseIcon } from '@heroicons/react/24/outline'
import { ChevronRightIcon, InformationCircleIcon } from '@heroicons/react/24/solid'
import cn from 'classnames'
import { type FC, useState } from 'react'
import Skeleton, { SkeletonTheme } from 'react-loading-skeleton'
import { PlaidLink } from '../../components'
import { type Config } from '../../config/config'

export const ExternalAccountsPage: FC = () => {
  const [isOpen, setIsOpen] = useState(false)

  const { t } = useTranslation('features-external-accounts')
  const config = configClient.getConfig() as Config
  const { data, isLoading, isError, isFetching } = useCustomerDetails()

  return (
    <>
      <PageHeader
        title={t('accounts.external_accounts')}
        subtitle={t('accounts.settings')}
        backLinkUrl={
          config.env === 'demo-dev' || config.env === 'demo-prod'
            ? WEALTH_ROUTES.TRANSFER.path
            : WEALTH_ROUTES.SETTINGS_BANK_TRANSFER_ACCOUNTS.backPath
        }
      />

      <div className="egw-container pt-0">
        {isLoading || isFetching ? (
          <SkeletonTheme baseColor="#eeefee" highlightColor="rgba(255, 255, 255, 0.5)">
            <Skeleton count={5} height="80px" />
          </SkeletonTheme>
        ) : null}

        {isError ? (
          <div className="egw-container">
            <InformationCircleIcon className="w-12 h-12 fill-red-500 mb-4" />
            <p className="text-error text-2xl font-light mb-10">{t('accounts.error_title')}</p>
            <p className="text-body text-xl">{t('accounts.error_message')}</p>
          </div>
        ) : null}

        {!isLoading && !isFetching && (
          <>
            {data?.customer.linkedAccounts.achAccounts && data.customer.linkedAccounts.achAccounts.length > 0 ? (
              <>
                <div className="flex flex-col">
                  {data.customer.linkedAccounts.achAccounts.map((account) => {
                    return (
                      <Link
                        href={`${WEALTH_ROUTES.SETTINGS_BANK_TRANSFER_ACCOUNTS_DETAILS.path}?alias=${account.alias.toString()}`}
                        key={account.alias}
                        testId={`externalAccount-${account.alias.toString()}-container_primary_btn`}
                        className={cn(
                          'flex items-center py-4 group border-l-[.188rem] pl-4 border-b border-light last-of-type:border-none',
                          {
                            'border-l-red-500': account.linkedAccountStatus === LinkedAccountStatus.EXPIRED,
                            'border-l-transparent': account.linkedAccountStatus !== LinkedAccountStatus.EXPIRED,
                          },
                        )}
                      >
                        {account.linkedAccountStatus === LinkedAccountStatus.EXPIRED ? (
                          <div className="flex flex-row items-center gap-1 mr-2xs">
                            <AlertErrorIcon />
                          </div>
                        ) : null}
                        <div className="flex-1">
                          <p className="text-text-header typo-primary-lg-semibold">{account.name}</p>
                          <p className="typo-primary-base-regular text-text-meta">{account.mask}</p>
                        </div>
                        <ChevronRightIcon className="w-5 h-5 fill-header opacity-50 group-hover:opacity-100" />
                      </Link>
                    )
                  })}
                </div>

                <Button
                  variant={ButtonVariant.Secondary}
                  onClick={() => {
                    setIsOpen(true)
                  }}
                  isDisabled={Boolean(data.customer.linkedAccounts.achAccounts.length > 2)}
                  className="my-lg"
                >
                  {t('accounts.add_account')}
                </Button>
              </>
            ) : (
              <div className="flex flex-row gap-2xs w-full">
                <div className="rounded-full p-2xs bg-background-light h-fit flex items-center justify-center mr-3xs">
                  <BriefcaseIcon className="w-7 h-7 text-text-icons-full flex-shrink-0 self-start" />
                </div>
                <div className="flex flex-row max-md:flex-col gap-2xs justify-between w-full">
                  <div className="flex flex-col">
                    <p className="text-text-header typo-primary-base-semibold">{t('accounts.no_accounts.title')}</p>
                    <p className="text-text-body typo-primary-base-regular">{t('accounts.no_accounts.message')}</p>
                  </div>
                  <div className="flex items-center">
                    <Button
                      className="w-fit"
                      size="small"
                      isDisabled={config.env === 'demo-dev' || config.env === 'demo-prod'}
                      onClick={() => {
                        if (config.env === 'demo-dev' || config.env === 'demo-prod') {
                          return
                        }
                        sendEvent('user_addAccount_click', {
                          eventSource: 'egWealth',
                          category: 'account-linking',
                          subCategory: 'intent',
                        })
                        setIsOpen(true)
                      }}
                    >
                      {t('accounts.no_accounts.cta')}
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      <ModalDrawer
        isOpen={isOpen}
        onClose={() => {
          setIsOpen(false)
        }}
        title={t('accounts.add_account')}
      >
        {isOpen ? (
          <PlaidLink
            onPlaidSuccess={() => {}}
            onClose={() => {
              sendEvent('eg_linkInstantly_fail', {
                eventSource: 'egWealth',
                category: 'instant-link',
                subCategory: 'result',
                failureReason: 'cancelled',
              })
              setIsOpen(false)
            }}
          />
        ) : null}
      </ModalDrawer>
    </>
  )
}
